import { useState } from "react";
import { CheckCircle2, HelpCircle, AlertCircle, Loader2, ScanSearch } from "lucide-react";

const STATUS_ICON = {
  ok: { Icon: CheckCircle2, color: "#1F6B6B", label: "Compliant" },
  stretch: { Icon: HelpCircle, color: "#C28A2E", label: "May stray" },
  unknown: { Icon: HelpCircle, color: "#9A9A9A", label: "Could not evaluate" },
  violation: { Icon: AlertCircle, color: "#B5462F", label: "Contravened" },
};

// Phase 2 — AI compliance findings panel. Surfaces the per-rule verdicts the
// Creative-Sample inspection produced, lets an admin override a flagged rule,
// and offers a Re-inspect affordance. 'unknown' findings are shown separately
// (the AI could not evaluate them) and do not count toward compliance.
export default function ComplianceFindings({ program, canManage, onOverride, onReinspect, inspecting }) {
  const sample = program.creative_sample || {};
  const status = sample.inspection_status;
  const findings = sample.inspection_findings || [];
  const overridden = new Set(sample.overridden_rule_ids || []);
  const [openRule, setOpenRule] = useState(null);

  const evaluable = findings.filter((f) => ["ok", "stretch", "violation"].includes(f.status));
  const unknowns = findings.filter((f) => f.status === "unknown");
  const c = program.compliance || {};

  const Header = () => (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-[#6B6358]">
        <span data-testid="compliance-findings-relevant"><b className="text-[#1A1F2C]">{c.relevant_count ?? evaluable.length}</b> relevant</span>
        <span data-testid="compliance-findings-violations"><b className="text-[#B5462F]">{c.non_compliant_count ?? 0}</b> non-compliant</span>
        <span data-testid="compliance-findings-overrides"><b className="text-[#1A1F2C]">{c.override_count ?? 0}</b> overrides</span>
      </div>
      {canManage && (sample.pdf_url) && (
        <button
          type="button"
          data-testid="compliance-findings-reinspect"
          onClick={onReinspect}
          disabled={inspecting || status === "inspecting"}
          className="inline-flex items-center gap-1.5 text-xs text-[#1A1F2C] border border-[#1A1F2C]/25 rounded-full px-3 py-1.5 hover:bg-[#1A1F2C]/[0.04] disabled:opacity-50"
        >
          {(inspecting || status === "inspecting")
            ? <><Loader2 size={13} className="animate-spin" /> Inspecting…</>
            : <><ScanSearch size={13} /> Re-inspect</>}
        </button>
      )}
    </div>
  );

  if (status === "inspecting" || inspecting) {
    return (
      <div data-testid="compliance-findings-panel" className="bg-[#1A1F2C]/[0.03] border border-[#1A1F2C]/10 rounded-lg p-4">
        <p className="text-sm text-[#C28A2E] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Inspecting the creative sample against brand rules…
        </p>
      </div>
    );
  }

  const Row = ({ f }) => {
    const meta = STATUS_ICON[f.status] || STATUS_ICON.unknown;
    const Icon = meta.Icon;
    const isOverridden = overridden.has(f.rule_id);
    const flaggable = f.status === "violation" || f.status === "stretch";
    const isOpen = openRule === f.rule_id;
    return (
      <div
        data-testid={`compliance-finding-${f.rule_id}`}
        className="border rounded-md"
        style={{ backgroundColor: `${meta.color}14`, borderColor: `${meta.color}40` }}
      >
        <button
          type="button"
          data-testid={`compliance-finding-${f.rule_id}-toggle`}
          onClick={() => flaggable && setOpenRule(isOpen ? null : f.rule_id)}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left ${flaggable ? "cursor-pointer" : "cursor-default"}`}
        >
          <Icon size={18} style={{ color: meta.color }} className="shrink-0" />
          <span className="text-sm text-[#1A1F2C] font-medium">{f.rule_type || f.rule_id}</span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6358]">{f.severity}</span>
          {isOverridden && (
            <span className="text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-[#6B6358]/15 text-[#6B6358]">Overridden</span>
          )}
          {flaggable && (
            <span className="ml-auto text-[10px] uppercase tracking-[0.14em] text-[#6B6358]">
              {isOpen ? "Hide" : "Details"}
            </span>
          )}
        </button>
        {flaggable && isOpen && (
          <div className="px-3 pb-3 pt-0 pl-10">
            {f.explanation && <p className="text-sm text-[#3D3A35]">{f.explanation}</p>}
            {f.evidence && (
              <p className="text-xs text-[#6B6358] mt-1 italic">Element involved: “{f.evidence}”</p>
            )}
            <p className="text-[10px] uppercase tracking-[0.14em] text-[#6B6358] mt-1.5">AI confidence: {f.confidence}</p>
            {canManage && (
              <button
                data-testid={`compliance-finding-${f.rule_id}-override`}
                onClick={() => onOverride(f.rule_id, !isOverridden)}
                className={`mt-2 px-3 py-1 text-[10px] uppercase tracking-[0.16em] rounded-full border ${
                  isOverridden ? "border-[#1F6B6B]/50 text-[#1F6B6B]" : "border-[#C28A2E]/50 text-[#C28A2E]"
                }`}
              >
                {isOverridden ? "Remove override" : "Override for this program"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="compliance-findings-panel" className="bg-[#1A1F2C]/[0.03] border border-[#1A1F2C]/10 rounded-lg p-4">
      <Header />
      {status === "skipped" && (
        <p className="text-sm text-[#C28A2E] mb-2">
          {sample.inspection_note || "Inspection could not run on this sample."}
        </p>
      )}
      {findings.length === 0 ? (
        <p className="text-sm text-[#6B6358]">
          {status === "clean" ? "No active brand rules were applicable to this sample." : "No rules were checked yet."}
        </p>
      ) : (
        <div className="space-y-1.5">
          {evaluable.map((f) => <Row key={f.rule_id} f={f} />)}
          {unknowns.length > 0 && (
            <details className="mt-2">
              <summary data-testid="compliance-findings-unknown-toggle" className="text-xs text-[#6B6358] cursor-pointer">
                {unknowns.length} rule{unknowns.length === 1 ? "" : "s"} could not be evaluated from the PDF (not counted)
              </summary>
              <div className="space-y-1.5 mt-2">
                {unknowns.map((f) => <Row key={f.rule_id} f={f} />)}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
